import * as React from 'react';
import { ApiHatasi, api } from '@/lib/api';
import { paraFormatla } from '@/lib/utils';

/**
 * Rezervasyon iptali.
 *
 * Kaporasi alinmis bir rezervasyon iptal edilirken paraya ne oldugu MUTLAKA
 * kayda gecer: iade mi edildi, otelde mi kaldi. Aksi halde kasa raporu ile
 * kasadaki para birbirini tutmaz ve aylar sonra "bu tahsilat neydi" diye
 * bakilir. Sunucu da kararsiz iptali reddediyor; ekran ayni kurali gosterir.
 */
export function RezervasyonIptal({
  rez,
  onKapat,
  onIptalEdildi,
}: {
  rez: { id: string; kod: string; ad: string; kaporaKurus: number; paraBirimi: string };
  onKapat: () => void;
  onIptalEdildi: (mesaj: string) => void;
}) {
  const kaporaVar = rez.kaporaKurus > 0;
  const [neden, setNeden] = React.useState('');
  const [karar, setKarar] = React.useState<'IADE' | 'OTELDE_KALDI' | null>(null);
  const [hata, setHata] = React.useState<string | null>(null);
  const [gonderiliyor, setGonderiliyor] = React.useState(false);

  React.useEffect(() => {
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && !gonderiliyor && onKapat();
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [onKapat, gonderiliyor]);

  const gonderilebilir = (!kaporaVar || karar !== null) && !gonderiliyor;

  async function gonder() {
    if (!gonderilebilir) return;
    setGonderiliyor(true);
    setHata(null);
    try {
      await api(`/api/rezervasyonlar/${rez.id}/iptal`, {
        method: 'POST',
        body: JSON.stringify({
          neden: neden.trim() || 'Belirtilmedi',
          kaporaKarari: kaporaVar ? karar : undefined,
        }),
      });
      onIptalEdildi(
        kaporaVar && karar === 'IADE'
          ? `${rez.ad} iptal edildi · kapora iade edildi`
          : kaporaVar
            ? `${rez.ad} iptal edildi · kapora otelde kaldı`
            : `${rez.ad} iptal edildi`,
      );
    } catch (e) {
      setHata(e instanceof ApiHatasi ? e.message : 'İptal edilemedi.');
      setGonderiliyor(false);
    }
  }

  const tutar = paraFormatla(rez.kaporaKurus, rez.paraBirimi);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-lacivert/60 [animation:fadein_.2s_both]" onClick={onKapat} role="presentation">
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          role="dialog"
          aria-modal="true"
          onClick={(e) => e.stopPropagation()}
          className="flex w-full max-w-[460px] flex-col gap-4 rounded-[20px] bg-white p-6 shadow-[0_40px_80px_-32px_rgba(11,21,48,.55)] [animation:popin_.3s_cubic-bezier(.2,.8,.2,1)_both]"
        >
          <div>
            <span className="text-[13px] font-semibold text-gri">{rez.kod}</span>
            <h2 className="text-[20px] font-bold tracking-[-.01em] text-lacivert">Rezervasyonu iptal et</h2>
            <p className="mt-0.5 text-[13px] text-gri">
              {rez.ad} · oda tekrar satışa açılacak.
            </p>
          </div>

          {kaporaVar && (
            <div className="flex flex-col gap-2.5 rounded-[14px] bg-[#fff5dc] p-4">
              <span className="text-[14px] font-bold text-[#b9840f]">
                Bu rezervasyonun {tutar} kaporası var
              </span>
              <span className="text-[13px] leading-relaxed text-[#8a6a1f]">
                Paraya ne olduğunu seçin; kasa kaydı buna göre düzeltilir.
              </span>
              <div className="mt-1 flex flex-col gap-2">
                {(
                  [
                    ['IADE', `${tutar} iade edildi`, 'Kasadan düşülür, gelir sayılmaz.'],
                    ['OTELDE_KALDI', `${tutar} otelde kaldı`, 'Gelir olarak durur (iptal bedeli).'],
                  ] as const
                ).map(([deger, baslik, aciklama]) => (
                  <label
                    key={deger}
                    className={
                      'flex cursor-pointer gap-3 rounded-[12px] border-[1.5px] bg-white p-3 transition ' +
                      (karar === deger ? 'border-marka-600' : 'border-cizgi hover:border-mavi-acik')
                    }
                  >
                    <input
                      type="radio"
                      name="kapora-karari"
                      checked={karar === deger}
                      onChange={() => setKarar(deger)}
                      className="sr-only"
                    />
                    <span
                      className={
                        'mt-0.5 grid size-4 flex-none place-items-center rounded-full border-[1.5px] ' +
                        (karar === deger ? 'border-marka-600' : 'border-cizgi')
                      }
                    >
                      {karar === deger && <span className="size-2 rounded-full bg-marka-600" />}
                    </span>
                    <span className="flex flex-col gap-0.5">
                      <span className="text-sm font-semibold text-lacivert">{baslik}</span>
                      <span className="text-[12.5px] text-gri">{aciklama}</span>
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}

          <label className="flex flex-col gap-1.5">
            <span className="flex items-baseline justify-between text-[12.5px] font-semibold text-gri">
              İptal nedeni <span className="font-medium">isteğe bağlı</span>
            </span>
            <input
              value={neden}
              onChange={(e) => setNeden(e.target.value)}
              placeholder="Misafir vazgeçti"
              autoFocus={!kaporaVar}
              className="w-full rounded-xl border-[1.5px] border-cizgi bg-white px-3.5 py-2.5 text-[15px] text-lacivert outline-none transition focus:border-marka-600 focus:shadow-[0_0_0_4px_rgba(31,79,216,.12)]"
            />
          </label>

          {hata && <p role="alert" className="rounded-xl bg-[#fdeceb] px-3.5 py-3 text-[13px] font-medium text-kirmizi">{hata}</p>}

          <div className="mt-1 flex gap-3">
            <button
              type="button"
              onClick={onKapat}
              disabled={gonderiliyor}
              className="flex-1 rounded-full border-[1.5px] border-cizgi bg-white py-3 text-[15px] font-semibold text-lacivert transition hover:border-gri"
            >
              Vazgeç
            </button>
            <button
              type="button"
              onClick={gonder}
              disabled={!gonderilebilir}
              className="flex flex-1 items-center justify-center gap-2 rounded-full bg-kirmizi py-3 text-[15px] font-semibold text-white transition hover:brightness-95 disabled:opacity-40"
            >
              {gonderiliyor && <span className="size-4 rounded-full border-2 border-white/35 border-t-white [animation:spin_.7s_linear_infinite]" />}
              {gonderiliyor ? 'İptal ediliyor…' : 'Rezervasyonu iptal et'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
